/**
 * Turning an edited profile into per-field change requests, and applying decisions back.
 *
 * ── Why a diff, rather than storing the edited object ─────────────────────────────────────
 *
 * The rule is that an employee's edit must not replace the approved value until HR agrees. The
 * obvious implementation — keep a second copy of the whole profile as "pending" — makes HR rule
 * on a document instead of on facts. An employee who moves house and mistypes their PAN in the
 * same sitting would force HR to reject both, and the correct new address with it.
 *
 * So the unit is the FIELD. Approving an address and querying a PAN are independent acts, the
 * audit trail records the question people actually ask ("when did this change and who agreed"),
 * and a stale draft cannot silently revert a field the employee never touched.
 *
 * ── Addressing ────────────────────────────────────────────────────────────────────────────
 *
 * Paths are dotted: `personal.dateOfBirth`, `address.permanent.city`. Rows in a repeating
 * section are addressed by their stable id, never their index —
 * `qualifications.<id>.institute`. With indices, deleting an earlier row silently re-points
 * every pending change below it at a different record, and the audit trail becomes fiction.
 *
 * Everything here is PURE. No network, no clock beyond an injected timestamp, no storage — the
 * same reasoning attendanceEngine.ts applies, and the same benefit: the rules can be tested
 * without standing anything up, and the identical function can run on the server.
 */

import {
  ApprovedProfileData,
  ChangeRequestStatus,
  EmployeeSelfServiceProfile,
  ProfileChangeRequest,
} from '../types/employeeProfile';

/** Human labels, so HR reads "Date of birth" rather than "personal.dateOfBirth". */
const FIELD_LABELS: Record<string, string> = {
  profilePhotoKey: 'Profile photo',
  dateOfBirth: 'Date of birth',
  gender: 'Gender',
  bloodGroup: 'Blood group',
  personalEmail: 'Personal email',
  mobileNumber: 'Mobile number',
  fathersName: "Father's name",
  mothersName: "Mother's name",
  maritalStatus: 'Marital status',
  marriageDate: 'Marriage date',
  spouseName: 'Spouse name',
  nationality: 'Nationality',
  line1: 'Address line 1',
  line2: 'Address line 2',
  city: 'City',
  district: 'District',
  state: 'State',
  country: 'Country',
  pincode: 'PIN code',
  presentSameAsPermanent: 'Present address same as permanent',
  name: 'Name',
  relationship: 'Relationship',
  qualification: 'Qualification',
  institute: 'Institute',
  fromYear: 'From year',
  toYear: 'To year',
  grade: 'Grade',
  companyName: 'Company name',
  designation: 'Designation',
  fromDate: 'From date',
  toDate: 'To date',
  lastDrawnSalary: 'Last drawn salary',
  reasonForLeaving: 'Reason for leaving',
  companyAddress: 'Company address',
  aadhaarNumber: 'Aadhaar number',
  panNumber: 'PAN',
  passportNumber: 'Passport number',
  passportExpiry: 'Passport expiry',
  drivingLicence: 'Driving licence',
  occupation: 'Occupation',
  isDependent: 'Dependent',
  nomineeName: 'Nominee name',
  sharePercentage: 'Share %',
  nominationFor: 'Nomination for',
  bankName: 'Bank name',
  branch: 'Branch',
  accountNumber: 'Account number',
  ifscCode: 'IFSC code',
  nameAsPerBank: 'Name as per bank',
  pfNumber: 'PF number',
  uanNumber: 'UAN',
  esiNumber: 'ESI number',
};

/** Which top-level keys are repeating sections, and therefore addressed by row id. */
const REPEATING = new Set(['qualifications', 'previousEmployment', 'family', 'nominations']);

/** Sections whose fields live one level down — address has permanent/present beneath it. */
const NESTED = new Set(['address']);

const labelFor = (leaf: string): string => FIELD_LABELS[leaf] || leaf;

/**
 * Comparable form of a value.
 *
 * Trimmed, and undefined/null/'' all collapse to null — the three are the same fact ("not
 * recorded") arriving by different routes, and treating them as distinct would raise a change
 * request every time a form round-tripped an untouched empty field.
 */
const norm = (value: any): string | null => {
  if (value === undefined || value === null) return null;
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  /*
   * An array or object leaf is recorded as JSON, and an EMPTY array is "not recorded".
   *
   * A section's `documentKeys` is the only leaf of this kind — the repeating sections take the
   * row-by-row path above and never reach here. `String(['a','b'])` would give `a,b`, which
   * reads as one value, cannot be told from a genuine comma, and is what applyChange would then
   * write back INTO the array field. JSON is also exactly what the Lambda's diffLeaves stores,
   * so an approval decided on the server and one previewed here describe the same change.
   *
   * `[]` collapsing to null matters because an untouched section arrives as `[]` from one page
   * and as `undefined` from another; without it, opening a form would propose a change from
   * "nothing" to "nothing" on every profile that has never had a document attached.
   */
  if (typeof value === 'object') {
    if (Array.isArray(value) && value.length === 0) return null;
    return JSON.stringify(value);
  }
  const text = String(value).trim();
  return text === '' ? null : text;
};

/**
 * The value to write when a change is applied — JSON parsed back into the shape it came from.
 *
 * The mirror of `norm`, and a port of valueForPath in backend/employee-profile/index.js. Without
 * it an approved `identifications.documentKeys` change would store the literal string
 * `["employee-documents/…"]` where an array belongs, and the next page to render that section
 * would call `.map` on a string.
 *
 * Only a value that LOOKS structured is parsed. A stored value beginning with `[` that is not
 * JSON is far more likely to be somebody's address than a corrupted array, so it is left alone.
 */
const parsed = (raw: string | null): any => {
  if (raw === null || raw === undefined) return raw;
  if (typeof raw !== 'string') return raw;
  if (!raw.startsWith('[') && !raw.startsWith('{')) return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
};

const sameValue = (a: any, b: any): boolean => norm(a) === norm(b);

interface DiffOptions {
  employeeEmail: string;
  submittedBy: string;
  submittedByRole: 'EMPLOYEE' | 'HR';
  now?: string;
  /** Injected so tests are deterministic; defaults to a random id. */
  idFactory?: () => string;
}

const defaultId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `chg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/**
 * Every field that differs between the approved data and what was edited.
 *
 * Returns change requests, already stamped with who proposed them. An HR-authored diff comes
 * back with `submittedByRole: 'HR'` and is expected to be applied immediately by the caller —
 * HR is the approving authority, so their own edit needs no second signature. The record is
 * still created, because the audit trail must show HR's changes alongside the employee's.
 */
export function diffProfile(
  approved: ApprovedProfileData,
  edited: ApprovedProfileData,
  options: DiffOptions
): ProfileChangeRequest[] {
  const now = options.now || new Date().toISOString();
  const nextId = options.idFactory || defaultId;
  const changes: ProfileChangeRequest[] = [];

  const push = (section: string, path: string, label: string, oldValue: any, newValue: any) => {
    changes.push({
      id: nextId(),
      employeeEmail: options.employeeEmail,
      section,
      path,
      label,
      oldValue: norm(oldValue),
      newValue: norm(newValue),
      submittedBy: options.submittedBy,
      submittedByRole: options.submittedByRole,
      submittedAt: now,
      status:
        options.submittedByRole === 'HR'
          ? ChangeRequestStatus.APPROVED
          : ChangeRequestStatus.PENDING,
      ...(options.submittedByRole === 'HR' ? { decidedBy: options.submittedBy, decidedAt: now } : {}),
    });
  };

  const compareFlat = (section: string, prefix: string, before: any, after: any) => {
    const keys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
    keys.forEach((key) => {
      if (sameValue((before || {})[key], (after || {})[key])) return;
      push(section, `${prefix}.${key}`, labelFor(key), (before || {})[key], (after || {})[key]);
    });
  };

  const sections = new Set([...Object.keys(approved || {}), ...Object.keys(edited || {})]);

  sections.forEach((section) => {
    const before = (approved as any)?.[section];
    const after = (edited as any)?.[section];

    if (REPEATING.has(section)) {
      const beforeRows: any[] = Array.isArray(before) ? before : [];
      const afterRows: any[] = Array.isArray(after) ? after : [];
      const beforeById = new Map(beforeRows.map((r) => [r.id, r]));
      const afterById = new Map(afterRows.map((r) => [r.id, r]));

      // Added rows: every populated field is a change from nothing.
      afterRows.forEach((row) => {
        const previous = beforeById.get(row.id);
        if (!previous) {
          Object.keys(row).forEach((key) => {
            if (key === 'id' || norm(row[key]) === null) return;
            push(section, `${section}.${row.id}.${key}`, labelFor(key), null, row[key]);
          });
          return;
        }
        compareFlat(section, `${section}.${row.id}`, previous, row);
      });

      /*
       * Removed rows are recorded as one change, not one per field.
       *
       * HR is being asked "may this employer be deleted", which is a single decision. Six
       * separate field-clearing requests would let it be half-approved, leaving a row that is
       * neither present nor gone.
       */
      beforeRows.forEach((row) => {
        if (afterById.has(row.id)) return;
        const summary =
          row.companyName || row.qualification || row.name || row.nomineeName || 'entry';
        push(section, `${section}.${row.id}`, `Remove ${labelFor(section)}`, summary, null);
      });
      return;
    }

    if (NESTED.has(section)) {
      const groups = new Set([
        ...Object.keys(before || {}),
        ...Object.keys(after || {}),
      ]);
      groups.forEach((group) => {
        const b = (before || {})[group];
        const a = (after || {})[group];
        if (typeof b === 'object' || typeof a === 'object') {
          compareFlat(section, `${section}.${group}`, b, a);
        } else if (!sameValue(b, a)) {
          push(section, `${section}.${group}`, labelFor(group), b, a);
        }
      });
      return;
    }

    // Scalar top-level field, e.g. profilePhotoKey.
    if (typeof after !== 'object' || after === null) {
      if (!sameValue(before, after)) push(section, section, labelFor(section), before, after);
      return;
    }

    compareFlat(section, section, before, after);
  });

  return changes;
}

/**
 * Write one approved change into the approved data, returning a new object.
 *
 * Never mutates: the caller holds the previous state for the audit entry, and a mutation here
 * would leave `oldValue` pointing at the value that had just been overwritten.
 *
 * A row-removal change (path with no leaf, on a repeating section) deletes the row. A change to
 * a row that no longer exists is skipped rather than resurrecting it — an approval that arrives
 * after the row was legitimately deleted should be a no-op, not a zombie.
 */
export function applyChange(
  approved: ApprovedProfileData,
  change: ProfileChangeRequest
): ApprovedProfileData {
  const parts = change.path.split('.');
  const next: any = { ...approved };
  const [section] = parts;

  if (REPEATING.has(section)) {
    const rows: any[] = Array.isArray((next as any)[section]) ? [...(next as any)[section]] : [];
    const rowId = parts[1];
    const leaf = parts[2];

    if (!leaf) {
      // Row removal.
      next[section] = rows.filter((r) => r.id !== rowId);
      return next;
    }

    const index = rows.findIndex((r) => r.id === rowId);
    if (index === -1) {
      // The row was added by this very change set — create it, carrying its id.
      rows.push({ id: rowId, [leaf]: parsed(change.newValue) });
    } else {
      rows[index] = { ...rows[index], [leaf]: parsed(change.newValue) };
    }
    next[section] = rows;
    return next;
  }

  if (parts.length === 1) {
    next[section] = parsed(change.newValue);
    return next;
  }

  if (parts.length === 2) {
    next[section] = { ...((next as any)[section] || {}), [parts[1]]: parsed(change.newValue) };
    return next;
  }

  // Three levels: address.permanent.city
  const [, group, leaf] = parts;
  next[section] = {
    ...((next as any)[section] || {}),
    [group]: { ...(((next as any)[section] || {})[group] || {}), [leaf]: parsed(change.newValue) },
  };
  return next;
}

/** Apply a set of approved changes in order. */
export const applyChanges = (
  approved: ApprovedProfileData,
  changes: ProfileChangeRequest[]
): ApprovedProfileData => changes.reduce((acc, change) => applyChange(acc, change), approved);

/**
 * What the employee should SEE in the form: approved values with their own pending edits laid on
 * top.
 *
 * Without this the form would reload showing the approved value and look as though the employee's
 * submission had been discarded — so they would enter it again, creating a duplicate request.
 *
 * Only their PENDING changes are overlaid. A rejected one deliberately falls back to the approved
 * value, so the field returns to a known-good state and the rejection note explains why.
 */
export const withPendingApplied = (
  profile: EmployeeSelfServiceProfile
): ApprovedProfileData =>
  applyChanges(
    profile,
    (profile.pendingChanges || []).filter((c) => c.status === ChangeRequestStatus.PENDING)
  );

/**
 * The overall status implied by a profile's pending set.
 *
 * Derived rather than stored, so it cannot drift from the changes it describes — a stored status
 * and a pending list that disagree is the bug this whole feature would be judged on.
 */
export function deriveStatus(
  profile: EmployeeSelfServiceProfile
): EmployeeSelfServiceProfile['status'] {
  const pending = (profile.pendingChanges || []).filter(
    (c) => c.status === ChangeRequestStatus.PENDING
  );
  const everApproved = (profile.history || []).some(
    (c) => c.status === ChangeRequestStatus.APPROVED
  );

  if (pending.length > 0) {
    return everApproved
      ? ('CHANGES_PENDING_APPROVAL' as EmployeeSelfServiceProfile['status'])
      : ('SUBMITTED' as EmployeeSelfServiceProfile['status']);
  }
  if (everApproved) return 'COMPLETED' as EmployeeSelfServiceProfile['status'];
  return profile.status;
}
